import { z } from 'zod';
import { IsoTimestamp, UserId } from './common';
import {
  Skill,
  Language,
  WorkHistoryEntry,
  EducationEntry,
  SeniorityLevel,
  TrackType,
  EducationLevel,
} from './user-profile';

export const WizardExtractedAnswerSeed = z.object({
  questionKey: z.string().min(1).max(64),
  // #742: no .min(1). The LLM occasionally emits "" for unknown-answer
  // slots (its "I don't know" output shape), and rejecting empties
  // sank the whole /v1/resume/parse extraction. Downstream code in
  // apps/api/src/handlers/resume.ts:239-247 already guards: an empty
  // seed-answer is a no-op (the merge-into-employment conditional only
  // writes when the existing slot is empty, so ""→"" never clobbers
  // real data). Max cap stays as the real guard.
  answer: z.string().max(500),
});
export type WizardExtractedAnswerSeed = z.infer<typeof WizardExtractedAnswerSeed>;

export const WizardExtractedProfile = z.object({
  fullName: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  workAuthorization: z
    .enum(['us_citizen', 'permanent_resident', 'visa_no_sponsorship', 'requires_sponsorship'])
    .optional(),
  currentTitle: z.string().optional(),
  yearsTotal: z.number().nonnegative().optional(),
  // Kept for backward compat — flat string list. New richer field is `skills`.
  topSkills: z.array(z.string()).max(20).default([]),
  // Extended fields — all optional, matching CandidateProfile shapes
  seniority: SeniorityLevel.optional(),
  track: TrackType.optional(),
  yearsInLeadership: z.number().nonnegative().optional(),
  largestTeamManaged: z.number().int().nonnegative().optional(),
  highestEducationLevel: EducationLevel.optional(),
  industries: z.array(z.string()).optional(),
  summary: z.string().max(2000).optional(),
  skills: z.array(Skill).optional(),
  languages: z.array(Language).optional(),
  certifications: z.array(z.string()).optional(),
  workHistory: z.array(WorkHistoryEntry).optional(),
  education: z.array(EducationEntry).optional(),
});
export type WizardExtractedProfile = z.infer<typeof WizardExtractedProfile>;

export const WizardExtractionSchema = z.object({
  extractedAt: IsoTimestamp,
  profile: WizardExtractedProfile,
  summaryDraft: z.string().max(2000),
  answerSeeds: z.array(WizardExtractedAnswerSeed).max(30).default([]),
});
export type WizardExtraction = z.infer<typeof WizardExtractionSchema>;

export const UserProfile = z.object({
  userId: UserId,
  email: z.string().email(),
  name: z.string().min(1).optional(),
  createdAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
  hasSeenWizard: z.boolean().default(false),
  wizardExtraction: WizardExtractionSchema.nullable().default(null),
  wizardLlmCalls: z.number().int().nonnegative().default(0),
});

export type UserProfile = z.infer<typeof UserProfile>;

/**
 * Input-shaped UserProfile (issue #307 / [[zod-default-output-vs-input]]).
 * `UserProfile` (the inferred output type) marks `.default()` fields
 * (`hasSeenWizard`, `wizardExtraction`, `wizardLlmCalls`) as REQUIRED
 * because the parsed output always has them. Code paths that WRITE a
 * fresh row (`putUser`, signup handlers) shouldn't need to populate
 * those defaults to satisfy the type — `z.input<...>` treats them as
 * optional, matching the actual runtime contract. Use this on input
 * sites; use `UserProfile` on output / consumer sites.
 */
export type UserProfileInput = z.input<typeof UserProfile>;

export const CreateUserRequest = z.object({
  name: z.string().min(1).optional(),
});

export const UpdateUserRequest = z.object({
  name: z.string().min(1).optional(),
  hasSeenWizard: z.boolean().optional(),
  wizardExtraction: WizardExtractionSchema.nullable().optional(),
  wizardLlmCalls: z.number().int().nonnegative().optional(),
});
export type UpdateUserRequest = z.infer<typeof UpdateUserRequest>;
